/**
 * useBackofficeClients
 * Hook para obtener la lista de clientes (perfiles financieros) del partner.
 * Usa el endpoint real GET /credit/backoffice/profiles con paginación server-side.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { listProfiles } from '@/services/credit-engine.service';
import { PARTNER_ID } from '@/constants';
import type { ProfilesListFilters, PaginationMeta } from '@/types/scoring.types';

const QUERY_KEYS = {
  clients: (filters?: ProfilesListFilters) =>
    ['clients', 'profiles', PARTNER_ID, filters ?? {}] as const,
};

const DEFAULT_PAGE_SIZE = 50;

export function useBackofficeClients(filters?: ProfilesListFilters) {
  const params: ProfilesListFilters = {
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    sort_by: 'score',
    sort_order: 'desc',
    ...filters,
  };

  const query = useQuery({
    queryKey: QUERY_KEYS.clients(params),
    queryFn: () => listProfiles(PARTNER_ID, params),
    // Mantiene la data previa visible mientras refetcha con un queryKey nuevo.
    // Sin esto, cada keystroke del buscador vacía la tabla y muestra skeleton.
    placeholderData: keepPreviousData,
  });

  const responseData = query.data?.data;
  const clients = responseData?.items ?? [];
  const pagination: PaginationMeta = responseData?.pagination ?? {
    page: params.page ?? 1,
    page_size: params.page_size ?? DEFAULT_PAGE_SIZE,
    total_items: 0,
    total_pages: 0,
  };

  return {
    clients,
    pagination,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => query.refetch(),
  };
}
